import { ApolloClient, ApolloLink, split, InMemoryCache } from '@apollo/client';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { env } from '../utils/env';
import { createUploadLink } from 'apollo-upload-client';
import omitDeep from 'omit-deep-lodash';
import { defaultApolloCache } from './defaultCacheData';
import i18next from 'i18next';
import SubscriptionWsLink from './SubsciptionWsLink';
import { link as networkStatusLink } from '@ts-private/apollo-tools/links/networkStatus';

let access_token;
let activeSocket;
// Create a WebSocket link:
let wsLink;

export const closeSocket = reason => {
  if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
    activeSocket.close(1000, reason);
  }
};

const getWsLink = () => {
  let currentAccessToken;
  if (!wsLink) {
    const wsClient = createClient({
      url: `${env.REACT_APP_GQL_WS_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
      keepAlive: 10000, // ping server every 10 seconds,
      connectionParams: async () => {
        currentAccessToken = access_token;
        return {
          authorization: access_token ? `Bearer ${access_token}` : '',
          'Accept-Language': i18next?.language
        };
      },
      on: {
        connected: socket => (activeSocket = socket),
        ping: async received => {
          if (!received) {
            if (activeSocket?.readyState === WebSocket.OPEN && currentAccessToken !== access_token) {
              setTimeout(() => {
                activeSocket.close(1000, 'Access token silent renew');
              }, 1000); // wait 1 second for the pong and then close the connection
            }
          }
        },
        closed: event => {
          console.log(`GraphQL WebSocket closed!: ${event.code} Reason: ${event.reason}`);
        }
      }
    });
    wsLink = new SubscriptionWsLink(wsClient);
    wsLink.setOnError(
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
          graphQLErrors.map(({ message, locations, path }) =>
            console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
          );
        if (networkError) console.log(`[Network error]: ${networkError}`);
      })
    );
  }
  return wsLink;
};

const httpLink = createUploadLink({
  uri: `${env.REACT_APP_GQL_HTTP_PROTOCOL}://${env.REACT_APP_GQL}/graphql`,
  headers: { 'Apollo-Require-Preflight': 'true' }, 
  onError: onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
    if (networkError) console.log(`[Network error]: ${networkError}`);
  })
});

const authLink = setContext(async (_, { headers, serviceName }) => {
  return {
    headers: {
      ...headers,
      authorization: access_token ? `Bearer ${access_token}` : '',
      'Accept-Language': i18next.language,
      service: serviceName
    }
  };
});

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, ['__typename']);
  }
  return forward(operation);
});

const link = () =>
  split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    getWsLink(),
    httpLink
  );

const retryLink = new RetryLink({
  delay: {
    initial: 200,
    max: 2000,
    jitter: true
  },
  attempts: {
    max: 3
  }
});

const myAppLink = () => ApolloLink.from([networkStatusLink, omitTypenameLink, retryLink, authLink.concat(link())]);

const cache = new InMemoryCache({
  typePolicies: {
    FieldDecimalPlace: { keyFields: ['fieldType'] },
    AssetHierarchyLevel: {
      keyFields: ['assetTypeId', 'level']
    },
    DocumentTemplate: {
      keyFields: ['documentTemplateId']
    },
    RequiredDocumentPropertiesItem: {
      keyFields: ['propertyId']
    },
    AutomaticStateTransitionListItem: {
      keyFields: ['automaticStateTransitionId']
    },
    AutomaticStateTransition: {
      keyFields: ['automaticStateTransitionId']
    },
    TransitionRule: {
      keyFields: ['automaticStateTransitionRuleId']
    },
    InitializationParameter: {
      keyFields: ['paramCode']
    },
    Authorization: {
      keyFields: ['webAuthorizationId']
    },
    AuthorizationXUserGroup: {
      keyFields: ['authorizationId', 'userGroupId']
    },
    AuthorizationXDocumentTypeXUserGroup: {
      keyFields: ['authorizationId', 'userGroupId', 'documentTypeId', 'documentStateId']
    },
    AuthorizationContextType: {
      keyFields: ['authorizationContextTypeId']
    },
    DocumentTypeXStateModel: {
      keyFields: ['documentTypeId', 'documentStateId']
    },
    AuthorizationWithRefines: {
      keyFields: ['webAuthorizationId']
    },
    PartnerTypeItem: {
      keyFields: ['partnerTypeId']
    },
    PartnerTypeWithConsents: {
      keyFields: ['partnerTypeId']
    },
    ConsentType: {
      keyFields: ['consentTypeId']
    },
    Report: {
      keyFields: ['reportId']
    },
    ReportType: {
      keyFields: ['value']
    },
    DictionaryEditorType: {
      keyFields: ['dictionaryItemId']
    },
    Property: {
      keyFields: ['id']
    },
    PropertyCategories: {
      keyFields: ['dictionaryItemId']
    },
    PropertyTypes: {
      keyFields: ['dictionaryItemId']
    },
    DictionaryPropertyContext: {
      keyFields: ['dictionaryItemId']
    },
    PropertyDetail: {
      keyFields: ['propertyDetailId']
    },
    PropertyUserGroup: {
      keyFields: ['propertyId', 'userGroupId']
    },
    GenericEntityBusinessState: {
      keyFields: ['stateId']
    },
    Dealer: {
      keyFields: ['dealerId']
    },
    Responsible: {
      keyFields: ['responsibleId']
    },
    ERPUser: {
      keyFields: ['userId']
    },
    AllocationPerson: {
      keyFields: ['personId']
    },
    CompetenceOrder: {
      keyFields: ['competenceOrderId']
    },
    RuleStatus: {
      keyFields: ['statusId']
    },
    ApplicationStatus: {
      keyFields: ['statusId']
    },
    EngineAttribute: {
      keyFields: ['engineAttributeId']
    },
    ScoringGrid: {
      keyFields: ['engineScoringId']
    },
    DocumentClause: {
      keyFields: ['clauseId']
    },
    DocumentClauseItem: {
      keyFields: ['clauseId']
    },
    ScoringCategory: {
      keyFields: ['scoringId']
    },
    ScoringCategoryItem: {
      keyFields: ['scoringId']
    },
    ScoringCategoryDetailItem: {
      keyFields: ['scoringDetailId']
    },
    DocumentTemplateItem: {
      keyFields: ['documentTemplateId']
    },
    DocumentTemplateFilter: {
      keyFields: ['filterId']
    },
    DocumentTemplateFilterValue: {
      keyFields: ['valueId']
    },
    DocumentTemplateXUserGroup: {
      keyFields: ['documentTemplateId', 'userGroupId']
    },
    DocumentTemplateXSignature: {
      keyFields: ['documentTemplateId', 'signatureId']
    },
    ContentType: {
      keyFields: ['code']
    },
    ContentTypeXUserGroup: {
      keyFields: ['contentTypeId', 'userGroupId']
    },
    Questionnaire: {
      keyFields: ['questionnaireId']
    },
    Question: {
      keyFields: ['questionId']
    },
    QuestionAction: {
      keyFields: ['questionActionId']
    },
    SignatureActors: {
      keyFields: ['actorId']
    },
    ApprovalTypeListItem: {
      keyFields: ['approvalTypeId']
    },
    ApprovalTypeItem: {
      keyFields: ['approvalTypeId']
    },
    ApprovalTypeLevelItem: {
      keyFields: ['approvalTypeLevelId']
    },
    ApprovalTypeEmployeeItem: {
      keyFields: ['approvalTypeEmployeeId']
    },
    ApprovalTypeUserGroupItem: {
      keyFields: ['approvalTypeUserGroupId']
    },
    ApprovalTypeDecisionReasonItem: {
      keyFields: ['decisionReasonId']
    },
    DecisionBehavior: {
      keyFields: ['decisionBehaviorId']
    },
    QuestionnaireListItem: {
      keyFields: ['questionnaireId']
    },
    ReportItem: {
      keyFields: ['reportId']
    },
    ReportFilterItem: {
      keyFields: ['reportFilterId']
    },
    ReportParameterItem: {
      keyFields: ['reportParameterId']
    },
    ReportXDocumentTypeItem: {
      keyFields: ['reportId', 'documentTypeId']
    },
    ReportXPartnerTypeItem: {
      keyFields: ['reportId', 'partnerTypeId']
    },
    ReportXUserGroupItem: {
      keyFields: ['reportId', 'userGroupId']
    },
    EngineScoringGrid: {
      keyFields: ['engineScoringId']
    },
    EngineScoringGridItem: {
      keyFields: ['engineScoringId']
    },
    EngineScoringGridIntervalItem: {
      keyFields: ['engineIntervalId']
    },
    EngineScoringGridRuleItem: {
      keyFields: ['engineRuleId']
    },
    EngineScoringGridKnockoutCriteriaItem: {
      keyFields: ['engineScoringKnockoutCriteriaId']
    },
    EngineScoringGridXExternalSystemItem: {
      keyFields: ['engineScoringXExternalSystemId']
    },
    EngineScoringXAttributeItem: {
      keyFields: ['engineScoringXAttributeId']
    },
    EngineAttributeItem: {
      keyFields: ['engineAttributeId']
    },
    EngineScoringGridClassificationItem: {
      keyFields: ['engineScoringClassificationId']
    },
    EngineScoringGridClassificationXEngineAttributeItem: {
      keyFields: ['engineScoringClassificationXEngineAttributeId']
    },
    EngineScoringGridClassificationXScoringItem: {
      keyFields: ['engineScoringClassificationXScoringId']
    },
    EngineScoringGridClassificationXFinancialIndicatorItem: {
      keyFields: ['engineScoringClassificationXFinancialId']
    },
    ScoringCategoriesDictionaryEntry: {
      keyFields: ['scoringId']
    },
    EngineAttributesListItem: {
      keyFields: ['engineAttributeId']
    },
    EngineAttributeOptions: {
      keyFields: ['engineAttributeId']
    },
    RequiredDocument: {
      keyFields: ['requiredDocTypeId']
    },
    RequiredDocumentItem: {
      keyFields: ['requiredDocTypeId']
    },
    VerificationTypeItem: {
      keyFields: ['verificationTypeId']
    },
    VerificationTypeXDocumentType: {
      keyFields: ['verificationTypeId', 'documentTypeId']
    },
    GenericFilterParam: {
      keyFields: ['paramId'],
      fields: {
        genericFilterParamXContexts: { merge: false }
      }
    },
    GenericFilterValue: {
      keyFields: ['id', 'name']
    },
    Item: {
      keyFields: ['id']
    },
    ItemProperty: {
      keyFields: ['itemId', 'propertyId']
    },
    PropertyValue: {
      keyFields: ['propertyValueId']
    },
    ExchangeRate: {
      keyFields: ['exchangeRateId']
    },
    ExchangeRateItem: {
      keyFields: ['exchangeRateId']
    },
    Bank: {
      keyFields: ['bankId']
    },
    FinancialProduct: {
      keyFields: ['productId']
    },
    LeasingCommission: {
      keyFields: ['commissionId']
    },
    LeasingInterestsList: {
      fields: {
        values: { merge: false }
      }
    },
    ContractType: {
      keyFields: ['contractTypeId']
    },
    DocumentTypeProperty: {
      keyFields: ['documentTypeId', 'propertyId']
    },
    DocumentTypePropertyItem: {
      keyFields: ['documentTypeId', 'propertyId']
    },
    CommissionCalculationMethodsList: {
      fields: {
        values: { merge: false }
      }
    },
    LeasingInterestTypeDetailsList: {
      fields: {
        values: { merge: false }
      }
    },
    FinancialProductItem: {
      keyFields: ['productId']
    },
    LeasingFinancialProductItem: {
      keyFields: ['productId']
    },
    FinancialProductXBusinessFlowItem: {
      keyFields: ['productId', 'flowId']
    },
    LeasingPromotion: {
      keyFields: ['promoId']
    },
    ScheduleType: {
      keyFields: ['scheduleTypeId']
    },
    DocumentLeasingIncomeSchema: {
      keyFields: ['schemaId']
    },
    InterestType: {
      keyFields: ['interestTypeId']
    },
    FinancialProductFilters: {
      keyFields: ['filterId']
    },
    DealerBranding: {
      keyFields: ['partnerId']
    },
    FinancialProductRuleEditItem: {
      keyFields: ['financialProductRuleId']
    },
    FinancialProductResidualValueGridItem: {
      keyFields: ['financialProductXResidualValueGridId']
    },
    FinancialProductWarrantyEditItem: {
      keyFields: ['leasingFinancialProductXWarrantyId']
    },
    FinancialProductSeasonalityTemplateItem: {
      keyFields: ['seasonalityTemplateId']
    },
    FinancialProductRuleEditFilterItem: {
      keyFields: ['filterId']
    },
    FinancialProductRuleEditFilterValueItem: {
      keyFields: ['financialProductRuleConditionValueId']
    },
    FinancialProductRuleEditOutputDataItem: {
      keyFields: ['financialProductRuleOutputDataId']
    },
    FinancialProductRuleEditOutputDictionaryList: {
      keyFields: ['financialProductRuleOutputDictionaryId']
    },
    FinancialProductInsurersEditItem: {
      keyFields: ['leasingFinancialProductXLeasInsurerId']
    },
    UserInfo: {
      keyFields: ['userId']
    },
    UserAdditionalData: {
      keyFields: ['userId']
    },
    FinancialProductRuleEditIncomeList: {
      keyFields: ['financialProductRuleIncomeId']
    },
    CompetenceOrderRule: {
      keyFields: ['competenceOrderRuleId']
    },
    Convention: {
      keyFields: ['productId']
    },
    InsuranceConventionRisk: {
      keyFields: ['productId', 'insuranceTypeId', 'riskTypeId']
    },
    InsuranceConventionInsurer: {
      keyFields: ['productId', 'insurerId']
    },
    Commission: {
      keyFields: ['productId', 'insuranceTypeId']
    },
    InsuranceConventionItemType: {
      keyFields: ['productId', 'itemTypeId']
    },
    InsuranceConventionDeductibleCost: {
      keyFields: ['deductibleCostId']
    },
    InsuranceConventionAdditionalData: {
      keyFields: ['insuranceConventionAdditionalDataId']
    },
    InsuranceAdditionalDataType: {
      keyFields: ['additionalDataTypeId']
    },
    InsuranceAdditionalDataTypeDetail: {
      keyFields: ['detailId']
    },
    AccountStatementItem: {
      keyFields: ['configId']
    },
    AccountStatementDetailItem: {
      keyFields: ['configDetailId']
    },
    PartnerBaseType: {
      keyFields: false
    },
    ResidualValueGridItems: {
      keyFields: ['gridId']
    },
    ScheduleTypeCellParamTemplateItem: {
      keyFields: ['scheduleTypeCellParamTemplateId']
    },
    ResidualValueGridDetails: {
      keyFields: ['gridId', 'itemId']
    },
    PartnerTypeProperty: {
      keyFields: ['partnerTypeId', 'propertyId']
    },
    AccountStatementIndicatorItem: {
      keyFields: ['statementIndicatorId']
    },
    LeasingExpenseType: {
      keyFields: ['expenseTypeId']
    }
  }
});

cache.restore(defaultApolloCache);

export function setAccessToken(accessToken) {
  access_token = accessToken;
}

let apolloClient;
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = new ApolloClient({
      link: myAppLink(),
      cache,
      onResetStore: async () => {
        apolloClient.cache.restore(defaultApolloCache);
      }
    });
  }
  return apolloClient;
};
